<script setup lang="ts">
import { computed, ref } from "vue";
import { useTranslation } from "i18next-vue";
import AnalysisGroupEntityList from "../analysis/AnalysisGroupEntityList.vue";
import EntityAddRow from "../cards/EntityAddRow.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";
import { Facade } from "@/drawing";
import type { ObservationEntityRef } from "@/drawing/managers/ObservationsManager";
import type { MarkerPhotoState } from "@/utils/plan-analysis/types";

/**
 * Lien d'une observation avec le plan, dans sa carte — même mécanique que
 * la carte d'un groupe d'analyse : la liste de ses entités (un tap les
 * montre sur le plan, « Retirer » les détache), la ligne d'ajout d'éléments,
 * puis ce qui est propre aux observations : sur la ligne de chaque marqueur
 * photo, s'il a sa photo ou non ; l'alerte des marqueurs horodatés
 * inexploitables, « Tout détacher » et l'accès à la popup des marqueurs.
 * L'AJOUT de photo ne passe pas par ici : il se fait via les cases d'attente
 * de la grille photos.
 */

const props = defineProps<{
  /** Entités du plan rattachées à l'observation. */
  entities: ObservationEntityRef[];
  /** Numéros de marqueur couverts par une photo de l'observation. */
  coveredNumbers: number[];
  /** Nb d'éléments sélectionnés sur le plan et pas encore rattachés. */
  extraSelectedCount: number;
  /** Mode « cliquez pour ajouter » actif sur cette carte. */
  addMode: boolean;
}>();

const emit = defineEmits<{
  (e: "entity-remove", handle: string): void;
  (e: "add-selected"): void;
  (e: "toggle-add-mode"): void;
  (e: "detach-all"): void;
  (e: "open-marker-picker"): void;
}>();

const { t } = useTranslation();

/**
 * Montrer UNE entité : zoom ET sélection, pour que la bbox se recale sur
 * elle. Un simple zoom laisserait la grille de l'ancienne sélection à des
 * coordonnées écran périmées (bbox fantôme jusqu'au prochain changement de
 * vue).
 */
const focusEntity = (handle: string): void => {
  if (Facade.isInitialized) Facade.selection.highlightByHandles([handle]);
};

/**
 * État photo de chaque marqueur numéroté, par entité : une photo de
 * l'observation porte-t-elle son numéro ? Affiché sur sa ligne.
 */
const photoStates = computed(() => {
  const states = new Map<string, MarkerPhotoState>();
  for (const entity of props.entities) {
    if (entity.markerNumber === null) continue;
    states.set(
      entity.handle,
      props.coveredNumbers.includes(entity.markerNumber) ? "covered" : "missing",
    );
  }
  return states;
});

/**
 * Marqueurs rattachés dont le libellé n'est pas un nombre (horodatage) : le
 * rattachement photo ne sait pas les exploiter, on le signale plutôt que de
 * les passer sous silence — leur ligne ne porte pas d'état photo.
 */
const unusableMarkerLabels = computed(() => {
  const labels = new Set<string>();
  for (const entity of props.entities) {
    if (entity.markerLabel && entity.markerNumber === null) {
      labels.add(entity.markerLabel.trim());
    }
  }
  return [...labels];
});

const showTimestampInfo = ref(false);
</script>

<template>
  <div class="observation-plan" @click.stop>
    <!-- Entités rattachées : un tap montre l'entité, « Retirer » la détache -->
    <AnalysisGroupEntityList
      v-if="entities.length"
      :entities="entities"
      :photo-states="photoStates"
      :remove-title="t('canvas:observations.card.removeEntity')"
      @select="focusEntity"
      @remove="emit('entity-remove', $event)"
    />
    <div v-else class="plan-label">
      {{ t("canvas:analysis.entityList.title", { count: 0 }) }}
    </div>

    <EntityAddRow
      :add-mode="addMode"
      :extra-selected-count="extraSelectedCount"
      @add-selected="emit('add-selected')"
      @toggle-add-mode="emit('toggle-add-mode')"
    />

    <!-- Marqueurs horodatés : inexploitables, donc sans état photo -->
    <button
      v-if="unusableMarkerLabels.length"
      type="button"
      class="timestamp-alert mt-2"
      @click.stop="showTimestampInfo = true"
    >
      <PhosphorIcon name="warning" :size="12" weight="fill" />
      {{
        t("canvas:observations.markerPicker.timestampAlert", {
          labels: unusableMarkerLabels.join(", "),
        })
      }}
    </button>

    <!-- Actions : tout détacher (s'il y a des entités), marqueurs -->
    <div class="d-flex flex-wrap align-items-center gap-1 mt-2">
      <button
        v-if="entities.length"
        type="button"
        class="ghost-btn danger"
        @click="emit('detach-all')"
      >
        <PhosphorIcon name="x" :size="14" weight="bold" />
        {{ t("canvas:observations.editor.plan.detachAll") }}
      </button>
      <button
        type="button"
        class="ghost-btn"
        @click="emit('open-marker-picker')"
      >
        <PhosphorIcon name="magnifying-glass" :size="14" weight="bold" />
        {{ t("canvas:observations.markerPicker.open") }}
      </button>
    </div>
  </div>

  <DRDialog
    v-model="showTimestampInfo"
    class="canvas-dialog"
    :title="t('canvas:observations.markerPicker.timestampTitle')"
    hide-footer
    :width="420"
  >
    <p class="mb-0 timestamp-message">
      {{ t("canvas:observations.markerPicker.timestampMessage") }}
    </p>
  </DRDialog>
</template>

<style lang="scss" scoped>
// Même libellé que les champs de la carte, quand la liste est vide.
.plan-label {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  opacity: 0.7;
  margin-bottom: 3px;
}

.timestamp-message {
  white-space: pre-line;
  line-height: 1.5;
}

.timestamp-alert {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  border-radius: 8px;
  background-color: rgba(var(--bs-danger-rgb), 0.18);
  color: #ffb3b3;
  font-size: 0.7rem;
  padding: 3px 8px;
  text-align: left;

  &:hover {
    background-color: rgba(var(--bs-danger-rgb), 0.28);
    color: #fff;
  }
}

.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  border-radius: var(--bs-border-radius);
  background-color: transparent;
  color: var(--card-accent-light, var(--bs-primary));
  font-size: 0.75rem;
  padding: 3px 7px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: color-mix(in srgb, var(--bs-primary) 15%, transparent);
    }
  }

  &.danger {
    color: #fca5a5;

    &:hover {
      background-color: color-mix(in srgb, #fca5a5 18%, transparent);
      color: #fecaca;
    }
  }
}
</style>
